import { Box, Button, Checkbox, Chip, Stack, Typography } from "@mui/material";
import PrintView from "src/components/shared/PrintView";
import ViewBtn from "src/components/shared/ViewBtn";
import { formatCurrency } from "src/helper/convertNum";
import { v4 as uuid } from "uuid";
import useInventoryNotes from "src/hooks/debt/useInventoryNotes";
import convertCurrency from "src/helper/convertCurrency";
import dayjs from "dayjs";
import useInventoryNotesSummary from "src/hooks/debt/useInventoryNotesSummary";
import { useState } from "react";
import { CONTEXT_TYPE, STATUS, TYPE } from "../const/constants";
import { renderDebtAmountByInventoryType } from "../utils/renderDebtAmountByInventoryType";
import useAppGlobal from "src/context/useAppGlobal";
import { DebtSalesPrintDialog } from "../DebtSales/Dialogs";
import DebtSalesPayment from "../DebtSales/Dialogs/DebtSalesPayment";

const statusMap = {
  paid: { label: "Đã thanh toán", bg: "#34C75922", color: "#34C759" },
  unpaid: { label: "Chưa thanh toán", bg: "#FFB30022", color: "#FFB300" },
  draft: { label: "Đã Đổi/Trả hàng", bg: "#34C75922", color: "#34C759" },
};

export default function useDebtSalesTab({ onPay, onViewDetail, isUnpaidTab = false, filter = {}, date } = {}) {
  const [checkedList, setCheckedList] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [pagi, setPagi] = useState({ limit: 50, skip: 0 });
  const { handleOpenDialog } = useAppGlobal();
  const restParamsQuery = Object.fromEntries(Object.entries(filter).filter(([_, value]) => value));
  const params = {
    ...restParamsQuery,
    ...pagi,
    contextType: CONTEXT_TYPE.ORDER,
    fromDate: date?.fromDate ?? undefined,
    toDate: date?.toDate ?? undefined,
    searchField: filter?.searchField && filter?.searchValue ? filter?.searchField : undefined,
    searchValue: filter?.searchValue ?? undefined,
  };
  const { data } = useInventoryNotes(params);

  const { data: summaryData } = useInventoryNotesSummary(params);

  const handleViewPrint = () => {};

  const handleRedirectToOrder = (item) => {
    window.open(`/orders/${item?.contextId}?warehouseId=${item?.warehouse?._id}`, "_blank");
  };

  const handleChangeCheckbox = (checked, item) => {
    if (checked) {
      setCheckedList((prev) => [...prev, item]);
    } else {
      setCheckedList((prev) => prev.filter((checkedItem) => checkedItem?._id !== item?._id));
    }
  };

  const handleChangeCheckboxAll = (checked) => {
    if (checked) {
      setCheckedList(data?.data?.map((item) => item));
    } else {
      setCheckedList([]);
    }
  };

  const handlePreview = (item) => {
    handleOpenDialog({
      title: "Xem phiếu xuất",
      size: "xl",
      content: <DebtSalesPrintDialog item={item} />,
    });
  };

  const handleOpenPayment = (item) => {
    handleOpenDialog({
      title: `Thanh toán công nợ ${item?.code ?? "N/a"}`,
      size: "sm",
      content: <DebtSalesPayment item={item} />
    })
  }

  const bodyList =
    data?.data?.map((item, index) => {
      const key = String(item.status).toLowerCase();
      const statusStyle = statusMap[key] || statusMap.unpaid;
      const isPaid = item.status === STATUS.PAID;
      const isExchanged = item.status === "exchanged";

      return {
        ...item,
        statusRaw: item.status,
        code: (
          <Typography
            sx={{ color: "primary", textDecoration: "underline", cursor: "pointer" }}
            onClick={() => handlePreview(item)}
          >
            {item?.code}
          </Typography>
        ),
        checkbox: (
          <Checkbox
            checked={checkedList.some((checkedItem) => checkedItem?._id === item?._id)}
            onChange={(_, checked) => handleChangeCheckbox(checked, item)}
          />
        ),
        stt: index + 1 + pagi?.skip,
        receiptCode: (
          <Typography
            component="a"
            href="#"
            color="primary"
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {item.receiptCode ?? "-"}
          </Typography>
        ),
        warehouse: item?.warehouse?.name ? (
          <Stack>
            <Typography>{item.warehouse?.name}</Typography>
            <Typography
              color="#989898"
              fontSize="8px"
            >
              {item?.createdAt ? dayjs(item?.createdAt).format("DD/MM/YYYY") : "-"}
            </Typography>
          </Stack>
        ) : (
          "-"
        ),
        salesStaff: <Typography>{item.sales?.fullName}</Typography>,
        shippingFee: (
          <>
            <Typography fontWeight="bold">{formatCurrency(item.fee)}</Typography>
            <Typography
              color="#989898"
              fontSize="8px"
              fontStyle="italic"
            >
              {convertCurrency(item.fee)} đồng
            </Typography>
          </>
        ),
        debt: renderDebtAmountByInventoryType(item.total, item.type ?? TYPE.OUTBOUND),
        paymentMethod: <Typography>{item.paymentLogMethod ? (item.paymentLogMethod === "Transfer" ? "Chuyển khoản" : "Tiền mặt") : "-"}</Typography>,
        orderCode: (
          <Typography
            component="a"
            href="#"
            color="primary"
            sx={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={() => handleRedirectToOrder?.(item)}
          >
            {item.contextCode}
          </Typography>
        ),
        status: (
          <Chip
            label={statusStyle.label}
            size="small"
            sx={{ background: statusStyle.bg, color: statusStyle.color, fontWeight: "bold", py: "16px" }}
          />
        ),
        action: isExchanged ? (
          <Box
            display="flex"
            gap="6px"
            justifyContent="center"
          >
            <ViewBtn handleView={() => onViewDetail?.(item)} />
            <PrintView handleView={() => handleViewPrint(item)} />
          </Box>
        ) : isPaid ? (
          item.type === TYPE.OUTBOUND ? (
            <Button
              variant="text"
              size="small"
              onClick={() => onViewDetail?.(item)}
              sx={{ minWidth: "140px", color: "#007AFF", backgroundColor: "#007AFF1A" }}
              color="info"
            >
              Chi tiết thanh toán
            </Button>
          ) : null
        ) : (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleOpenPayment(item)}
            sx={{ minWidth: "100px" }}
          >
            Thanh toán
          </Button>
        ),
      };
    }) ?? [];

  const header = [
    {
      id: uuid(),
      title: (
        <Checkbox
          checked={checkedList.length >= data?.data?.length}
          onChange={(_, checked) => handleChangeCheckboxAll(checked)}
        />
      ),
      align: "center",
    },
    { id: uuid(), title: "STT", align: "center" },
    { id: uuid(), title: "Mã PN/PX", align: "center" },
    { id: uuid(), title: isUnpaidTab ? "Kho xuất" : "Kho Xuất/ Nhập", align: "center" },
    { id: uuid(), title: "Nhân viên KD", align: "center" },
    { id: uuid(), title: "Phí ship", align: "center" },
    { id: uuid(), title: "Công nợ", align: "center" },
    { id: uuid(), title: "Hình thức thanh toán", align: "center" },
    { id: uuid(), title: "Mã ĐH", align: "center" },
    { id: uuid(), title: "Trạng thái", align: "center" },
    { id: uuid(), title: "Thao tác", align: "center" },
  ];

  const sortData = ["checkbox", "stt", "code", "warehouse", "salesStaff", "shippingFee", "debt", "paymentMethod", "orderCode", "status", "action"];
  const footerTotals = {
    shippingFee: summaryData?.data?.totalShipping ?? 0,
    debt: summaryData?.data?.totalDebt ?? 0,
  };

  return { bodyList: bodyList ?? [], pagi, setPagi, header, total: data?.count ?? 0, sortData, footerTotals, selectedRow, checkedList, setSelectedRow };
}
